import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, CircularProgress, Link, Typography } from '@mui/material';
import { IOrderDashboardRow } from '../../../interfaces/garments/merchReport.interface';
import { ProductionOutput } from '../../../interfaces/costing.interface';
import OrderThread from '../../hero/OrderThread';
import GarmentFill from '../../hero/GarmentFill';
import SpoolGauge from '../../hero/SpoolGauge';
import { useAuth } from '../../../context/AuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { isLate, journeyPath, progressText, rowToSteps, shortDate, sortThreadRows } from './threadData';
import { useProductionOutputs, useThreadOrders } from './useThreadData';

const num = (n: number) => n.toLocaleString('en-US');
const MAX_ROWS = 12;

/** The lead order drawn big: the garment filling up and the finished spool, when the viewer may see output. */
export function LeadOrderHero({ row, outputs }: { row: IOrderDashboardRow; outputs: ProductionOutput[] | null }) {
    const qty = row.totalQuantity ?? 0;
    const totals = useMemo(() => {
        const t = { CUT: 0, SEWN: 0, FINISHED: 0 };
        for (const o of outputs ?? []) t[o.stage] += o.quantity;
        return t;
    }, [outputs]);
    const hasOutput = (outputs ?? []).length > 0;
    const seen = outputs !== null;
    const share = (n: number) => (qty > 0 ? n / qty : 0);
    const finishedPct = seen && hasOutput && qty > 0 ? (totals.FINISHED / qty) * 100 : null;

    return (
        <Box data-testid="thread-lead" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <GarmentFill
                cut={seen && hasOutput ? share(totals.CUT) : undefined}
                sewn={seen && hasOutput ? share(totals.SEWN) : undefined}
                finished={seen && hasOutput ? share(totals.FINISHED) : undefined}
                width={110}
            />
            {seen && (
                <SpoolGauge
                    value={finishedPct}
                    label="Finished"
                    caption={finishedPct != null ? `${num(totals.FINISHED)} of ${num(qty)} pcs` : undefined}
                    width={150}
                />
            )}
            <Box sx={{ minWidth: 0, flex: '1 1 160px' }}>
                <Typography variant="caption" color="text.secondary">Lead order</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{row.orderNumber}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    {row.buyerName} · ships {shortDate(row.shipDate)}
                </Typography>
            </Box>
        </Box>
    );
}

interface ListProps {
    rows: IOrderDashboardRow[] | null;
    loading: boolean;
    error: boolean;
    truncated?: boolean;
    /** Outputs of the lead order; omit (undefined) to leave the hero block out. */
    leadOutputs?: ProductionOutput[] | null;
    showHero?: boolean;
}

/** All live orders as compact threads, late first. Each row opens that order's thread page. */
export function OrderThreadsList({ rows, loading, error, truncated, leadOutputs = null, showHero = false }: ListProps) {
    const { hasPermission } = useAuth();
    const canOpen = hasPermission(PERMISSIONS.BUYER_ORDER_VIEW);
    const sorted = useMemo(() => (rows ? sortThreadRows(rows) : []), [rows]);

    if (loading && rows === null) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} aria-label="Loading order threads" /></Box>;
    }
    if (error) return <Alert severity="warning">Could not load the order threads.</Alert>;
    if (rows === null) return null;
    if (sorted.length === 0) return <Typography variant="body2" color="text.secondary">No live orders right now.</Typography>;

    const shown = sorted.slice(0, MAX_ROWS);
    const hidden = sorted.length - shown.length;
    return (
        <Box data-testid="thread-orders">
            {showHero && <LeadOrderHero row={shown[0]} outputs={leadOutputs} />}
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 }}>
                {shown.map(r => (
                    <Box component="li" key={r.orderId}>
                        {/* the thread page needs BUYER_ORDER_VIEW; without it the row is plain text, not a dead link */}
                        <Box
                            {...(canOpen ? { component: RouterLink, to: journeyPath(r.orderId), 'aria-label': `Open the thread of order ${r.orderNumber}, ${progressText(r)}` } : {})}
                            data-help="dashboard.thread.orderRow"
                            sx={{
                                display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, rowGap: 0.25, px: 1, py: 0.75,
                                borderRadius: 1, color: 'inherit', textDecoration: 'none', ...(canOpen ? { '&:hover': { bgcolor: 'action.hover' } } : {}),
                            }}
                        >
                            <Box sx={{ flex: '1 1 130px', minWidth: 0, maxWidth: 220 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{r.orderNumber}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{r.buyerName}</Typography>
                            </Box>
                            <Box sx={{ flex: '2 1 160px', minWidth: 0 }}>
                                <OrderThread steps={rowToSteps(r)} compact name={`Order ${r.orderNumber}`} emptyText="No T&A calendar yet." />
                            </Box>
                            <Box sx={{ flex: '0 1 120px', textAlign: { sm: 'right' } }}>
                                <Typography variant="body2" color={isLate(r) ? 'error.main' : 'text.primary'} sx={{ fontWeight: isLate(r) ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>
                                    {progressText(r)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Ships {shortDate(r.shipDate)}</Typography>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
            {(hidden > 0 || truncated) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {hidden > 0 ? `${hidden} more live orders. ` : ''}
                    {truncated ? 'The list was cut at the report limit. ' : ''}
                    {hasPermission(PERMISSIONS.BUYER_ORDER_VIEW) && (
                        <Link component={RouterLink} to="/merchandising/orders" data-help="dashboard.thread.viewAll">See all orders</Link>
                    )}
                </Typography>
            )}
        </Box>
    );
}

/**
 * Registry widget body (merchandising.orderThreads): loads its own rows. The board does not use this;
 * it loads the rows once and shares them between its sections.
 */
export default function OrderThreadsWidget({ institutionId }: { institutionId: string }) {
    const { hasPermission } = useAuth();
    const orders = useThreadOrders(institutionId, hasPermission(PERMISSIONS.MERCH_REPORT_VIEW));
    return <OrderThreadsList rows={orders.rows} loading={orders.loading} error={orders.error} truncated={orders.truncated} />;
}

/** Output rows for the lead order, when the caller may read output. Used by the board. */
export function useLeadOutputs(institutionId: string, rows: IOrderDashboardRow[] | null): ProductionOutput[] | null {
    const { hasPermission } = useAuth();
    const lead = useMemo(() => (rows && rows.length > 0 ? sortThreadRows(rows)[0].orderId : null), [rows]);
    const params = hasPermission(PERMISSIONS.COSTING_VIEW) && lead ? { buyerOrderId: lead } : null;
    return useProductionOutputs(institutionId, params);
}
